'use client';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * React bindings of the experiments client (`@repo/experiments/react`).
 *
 *   <ExperimentsProvider client={experiments}>…</ExperimentsProvider>   once, at the app root
 *   const { variant } = useExperiment('search_card_v2')                  a test decided at render: logs its exposure
 *   const { variant } = useVariant('guest_tour_booking')                 read only, no exposure
 *   const experiments = useExperiments(); experiments.enter(key)         a test decided at a tap
 *
 * Server rendering always gets the default experience (variant null): Sucuri caches pages, so a variant must never
 * be baked into HTML. The browser re-renders with the visitor's variant as soon as it is known (at once for a
 * returning visitor, whose last answer is cached).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore, } from 'react';
/** The server render's answer: not known yet, so a render test shows its placeholder. */
const NO_ASSIGNMENT = (key) => ({
    key,
    variant: null,
    enrolled: false,
    forced: false,
    payload: {},
    ready: false,
});
/** What a component gets with no provider above it: nothing is running, nothing is sent. */
const NOOP = {
    ready: () => Promise.resolve(),
    get: (key) => ({ ...NO_ASSIGNMENT(key), ready: true }), // nothing will ever run: show the default
    enter: () => null,
    track: () => { },
    identify: () => Promise.resolve(),
    refresh: () => Promise.resolve(),
    flush: () => Promise.resolve(),
    subscribe: () => () => { },
    debug: () => ({
        ready: false,
        source: 'none',
        configVersion: null,
        subjectId: null,
        assignments: {},
        overrides: {},
        exposed: [],
        queued: { exposures: 0, events: 0 },
        lastError: 'no ExperimentsProvider',
    }),
};
const Context = createContext(NOOP);
export function ExperimentsProvider({ client, children, }) {
    return _jsx(Context.Provider, { value: client, children: children });
}
/** The page's client, for `enter()` at a tap, `track()` and `identify()`. */
export function useExperiments() {
    return useContext(Context);
}
/** The visitor's assignment for `key`, re-rendering when it changes. Logs nothing. */
export function useVariant(key) {
    const client = useExperiments();
    const subscribe = useCallback((listener) => client.subscribe(listener), [client]);
    const fingerprint = useSyncExternalStore(subscribe, () => JSON.stringify(client.get(key)), () => '');
    return useMemo(() => (fingerprint ? client.get(key) : NO_ASSIGNMENT(key)), [client, key, fingerprint]);
}
/**
 * For a test decided when the component renders (a card design, a copy change): the assignment, and one exposure
 * logged after the variant has actually rendered. For a test decided at a tap, call `enter()` in the handler instead.
 */
export function useExperiment(key, options = {}) {
    const client = useExperiments();
    const assignment = useVariant(key);
    const entryPoint = options.entryPoint;
    useEffect(() => {
        if (assignment.variant != null)
            client.enter(key, entryPoint ? { entryPoint } : {});
    }, [client, key, assignment.variant, entryPoint]);
    return assignment;
}
